import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Check, ChevronDown, LoaderCircle, Search } from 'lucide-react';

import { cn } from '../../lib/cn';
import { Tooltip } from '../Tooltip';
import styles from './Combobox.module.css';

export type ComboboxItem = {
  readonly id: string;
  readonly label: string;
  readonly description?: string;
  readonly leading?: ReactNode;
};

export type ComboboxProps = {
  readonly items: readonly ComboboxItem[];
  readonly value: readonly string[];
  readonly onChange: (value: readonly string[]) => void;
  readonly placeholder?: string;
  readonly label?: string;
  readonly multiple?: boolean;
  readonly isLoading?: boolean;
  readonly emptyText?: string;
  readonly ariaLabel?: string;
};

/** Комбобокс для пользователей, эпиков и меток (docs/06 §4). */
export function Combobox({
  items,
  value,
  onChange,
  placeholder = 'Выбрать',
  label,
  multiple = false,
  isLoading = false,
  emptyText = 'Ничего не найдено',
  ariaLabel,
}: ComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const listId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);

  const selectedItems = useMemo(
    () => value.map((id) => items.find((item) => item.id === id)).filter((item): item is ComboboxItem => Boolean(item)),
    [items, value],
  );

  const filtered = useMemo(() => {
    if (query.trim().length === 0) {
      return items;
    }
    const needle = query.trim().toLowerCase();
    return items.filter(
      (item) => item.label.toLowerCase().includes(needle) || item.id.toLowerCase().includes(needle),
    );
  }, [items, query]);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      return;
    }
    const onPointerDown = (event: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const toggle = (id: string): void => {
    if (value.includes(id)) {
      onChange(value.filter((item) => item !== id));
      return;
    }
    onChange(multiple ? [...value, id] : [id]);
    if (!multiple) {
      setIsOpen(false);
    }
  };

  const triggerLabel = selectedItems.length === 0 ? placeholder : selectedItems.map((item) => item.label).join(', ');

  return (
    <div className={styles['field']} ref={rootRef}>
      {label ? <span className={styles['label']}>{label}</span> : null}
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-label={ariaLabel ?? label ?? placeholder}
        className={cn(styles['trigger'], isOpen && styles['open'])}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className={cn(styles['value'], selectedItems.length === 0 && styles['placeholder'])}>{triggerLabel}</span>
        <ChevronDown aria-hidden size={16} className={cn(styles['chevron'], isOpen && styles['chevronOpen'])} />
      </button>

      {isOpen ? (
        <div className={styles['popover']}>
          <div className={styles['search']}>
            <Search aria-hidden size={14} />
            <input
              autoFocus
              value={query}
              placeholder="Поиск"
              aria-label="Поиск"
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <ul className={styles['list']} id={listId} role="listbox" aria-multiselectable={multiple}>
            {isLoading ? (
              <li className={styles['state']}>
                <LoaderCircle aria-hidden size={14} className={styles['spinner']} /> Загрузка…
              </li>
            ) : filtered.length === 0 ? (
              <li className={styles['state']}>{emptyText}</li>
            ) : (
              filtered.map((item) => {
                const isSelected = value.includes(item.id);
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={cn(styles['option'], isSelected && styles['optionActive'])}
                      onClick={() => toggle(item.id)}
                    >
                      {item.leading ?? <span aria-hidden className={styles['checkSlot']}>{isSelected ? <Check size={14} /> : null}</span>}
                      <span className={styles['optionLabel']}>
                        {item.label}
                        {item.description ? <small>{item.description}</small> : null}
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      ) : null}
      {multiple && selectedItems.length > 0 ? (
        <div className={styles['chips']}>
          {selectedItems.map((item) => (
            <span key={item.id} className={styles['chip']}>
              {item.label}
              <Tooltip content="Убрать">
                <button type="button" aria-label={`Убрать ${item.label}`} onClick={() => toggle(item.id)}>
                  ×
                </button>
              </Tooltip>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
